import Box from '@mui/material/Box';
import { alpha, type Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { useTranslation } from 'react-i18next';

import {
  type CriteriaResult,
  RATING_SCALE_MAX,
  type ResultTone,
  resultTone,
} from './ratingSummary';

const toneSx: Record<ResultTone, SystemStyleObject<Theme>> = {
  good: {
    color: 'success.main',
    bgcolor: (theme) => alpha(theme.palette.success.main, 0.14),
  },
  fair: {
    color: 'warning.main',
    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.14),
  },
  poor: {
    color: 'error.main',
    bgcolor: (theme) => alpha(theme.palette.error.main, 0.14),
  },
  neutral: { color: 'text.primary', bgcolor: 'action.selected' },
  muted: { color: 'text.secondary', bgcolor: 'transparent' },
};

const useResultLabel = (result: CriteriaResult): string => {
  const { t } = useTranslation();
  switch (result.kind) {
    case 'score':
      return `${result.score}/${RATING_SCALE_MAX}`;
    case 'yesNo':
      return t(
        result.passed ? 'ratingDetail.result.yes' : 'ratingDetail.result.no'
      );
    case 'choice':
      return result.label;
    case 'recorded':
      return t('ratingDetail.result.recorded');
    case 'notRated':
      return t('ratingDetail.result.notRated');
    default: {
      const exhaustive: never = result;
      return exhaustive;
    }
  }
};

export const RatingResultBadge = ({
  result,
}: {
  result: CriteriaResult;
}): JSX.Element => {
  const label = useResultLabel(result);

  return (
    <Box
      component="span"
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          flexShrink: 0,
          maxWidth: 160,
          height: 24,
          px: 1,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        toneSx[resultTone(result)],
      ]}
    >
      {label}
    </Box>
  );
};
